const CHAR_WIDTH_PX = 8;
const WIDTH_SAFETY = 1.3;
const NAME_LINE_HEIGHT_PX = 18;
const DIMENSIONS_LINE_HEIGHT_PX = 14;
const BTU_ROW_HEIGHT_PX = 22;
export const BTU_ROW_WIDTH_PX = 140;
const TOGGLE_WIDTH_PX = 44;
const TOGGLE_ROW_HEIGHT_PX = 32;
const MIN_LABEL_WIDTH_PX = 40;
const DEFAULT_DIMENSIONS_TEXT = '99ft × 99ft';

function safeLineWidth(longestLine: number, maxWidth: number): number {
  return Math.min(
    maxWidth,
    Math.max(MIN_LABEL_WIDTH_PX, Math.ceil(longestLine * WIDTH_SAFETY))
  );
}

export function formatSpaceLabelDimensions(args: {
  widthPx: number;
  heightPx: number;
  squareFootage?: number | null;
}): string {
  if (args.squareFootage != null) {
    return `${Math.round(args.squareFootage)} sq ft`;
  }
  return `${Math.round(args.widthPx / 10)}ft × ${Math.round(
    args.heightPx / 10
  )}ft`;
}

export function measureWrappedLabel(
  label: string,
  maxWidth: number
): { width: number; height: number; lines: number } {
  const words = label.split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) {
    return { width: MIN_LABEL_WIDTH_PX, height: NAME_LINE_HEIGHT_PX, lines: 1 };
  }

  let lines = 1;
  let lineWidth = 0;
  let longestLine = 0;

  words.forEach((word) => {
    const wordWidth = word.length * CHAR_WIDTH_PX;
    const nextWidth =
      lineWidth === 0 ? wordWidth : lineWidth + CHAR_WIDTH_PX + wordWidth;
    if (lineWidth > 0 && nextWidth > maxWidth) {
      lines += 1;
      lineWidth = wordWidth;
    } else {
      lineWidth = nextWidth;
    }
    if (lineWidth > longestLine) {
      longestLine = lineWidth;
    }
  });

  return {
    width: safeLineWidth(longestLine, maxWidth),
    height: lines * NAME_LINE_HEIGHT_PX,
    lines
  };
}

export function measureUnwrappedLabelWidth(label: string): number {
  const words = label.split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) {
    return MIN_LABEL_WIDTH_PX;
  }
  const rawWidth =
    words.reduce((total, word) => total + word.length, 0) * CHAR_WIDTH_PX +
    Math.max(0, words.length - 1) * CHAR_WIDTH_PX;
  return Math.max(MIN_LABEL_WIDTH_PX, Math.ceil(rawWidth * WIDTH_SAFETY));
}

export function labelHasUnwrappableOverflow(
  label: string,
  maxWidth: number
): boolean {
  const words = label.split(/\s+/).filter((word) => word.length > 0);
  return words.some((word) => word.length * CHAR_WIDTH_PX > maxWidth);
}

export function estimateSpaceLabelSize(
  label: string,
  options?: {
    showDimensions?: boolean;
    dimensionsText?: string;
    showBtus?: boolean;
    showToggle?: boolean;
    maxWidth?: number;
  }
): { width: number; height: number } {
  const maxWidth = options?.maxWidth ?? 200;
  const wrapped = measureWrappedLabel(label, maxWidth);
  let { width, height } = wrapped;
  if (options?.showDimensions) {
    height += DIMENSIONS_LINE_HEIGHT_PX;
    const dimensionsText = options.dimensionsText ?? DEFAULT_DIMENSIONS_TEXT;
    width = Math.max(
      width,
      safeLineWidth(dimensionsText.length * CHAR_WIDTH_PX, maxWidth)
    );
  }
  if (options?.showBtus) {
    height += BTU_ROW_HEIGHT_PX;
    width = Math.max(width, BTU_ROW_WIDTH_PX);
  }
  if (options?.showToggle) {
    height += TOGGLE_ROW_HEIGHT_PX;
    width = Math.max(width, TOGGLE_WIDTH_PX);
  }
  return { width, height };
}
